import { useEffect, useState, memo } from "react";
import { BarChart, Bar, XAxis, ResponsiveContainer, ReferenceLine, LabelList, Cell, Legend, YAxis } from "recharts";
import axiosInterceptorInstance from "@/elements/axiosInterceptorInstance";
import { DurationBtn } from "@/elements/Button/Button";
import Actions from "@/elements/Actions";

function BalanceSheetCard(props) {
    const [data, setData] = useState([]);
    const [duration, setDuration] = useState(0);

    const fetchData = (duration = 0) => {
        axiosInterceptorInstance
            .get(
                `/resources/stock-details/${props.name}/company-financials/${
                    duration === 0 ? "standalone" : "consolidated"
                }/${props.indicator}`
            )
            .then((response) => {
                let dataArray = response?.data?.data?.reverse()?.map((element) => ({
                    duration: element.duration,
                    assets: element.assets,
                    liabilities: element.liabilities
                }));
                setData(dataArray);
            });
    };

    useEffect(() => {
        fetchData();
    }, [props]);

    const processedData = data.map((item) => ({
        ...item,
        dummyAssets: Math.max(...data?.map((item) => Math.abs(item.assets))) * 0.02,
        dummyLiabilities: Math.max(...data?.map((item) => Math.abs(item.liabilities))) * 0.02
    }));

    const renderCustomLegend = (props) => {
        const { payload } = props;

        return (
            <ul className="flex">
                {payload
                    .filter((entry) => entry.value !== "dummyAssets" && entry.value !== "dummyLiabilities")
                    .map((entry, index) => (
                        <li key={`item-${index}`} className="flex items-center mr-4">
                            <svg width="10" height="10" className="mr-1">
                                <rect
                                    width="10"
                                    height="10"
                                    rx="2"
                                    ry="2"
                                    fill={entry.value == "assets" ? "#2ACF56" : "#F00"}
                                />
                            </svg>
                            <span className="text-xs font-medium">
                                {entry.value ? entry.value.slice(0, 1).toUpperCase() + entry.value.slice(1) : ""}
                            </span>
                        </li>
                    ))}
            </ul>
        );
    };

    return (
        <div className="font-Inter font-medium mx-3">
            <ResponsiveContainer width="100%" height={228}>
                <BarChart data={processedData} margin={{ top: 32, right: 0, left: 0, bottom: 8 }}>
                    <XAxis
                        axisLine={false}
                        tickLine={false}
                        dataKey="duration"
                        tick={{
                            color: "#979797"
                        }}
                        tickMargin={16}
                        className="font-Inter font-semibold text-xs"
                    />
                    <Legend content={renderCustomLegend} verticalAlign="top" align="left" height={36} />
                    <Bar dataKey="assets" stackId="a" radius={[2, 2, 0, 0]}>
                        <LabelList
                            dataKey="assets"
                            position="center"
                            className="font-Inter font-semibold text-xs"
                            content={(props) => {
                                const { x, y, value } = props;
                                const formattedValue = Actions.trimNumber(value / 10000000, 1);
                                return (
                                    <text
                                        x={x + 20}
                                        y={y}
                                        dy={-8}
                                        fontSize="12"
                                        fill={value >= 0 ? "black" : "#DD2C2C"}
                                        textAnchor="middle"
                                        fontWeight={600}
                                    >
                                        {formattedValue}
                                    </text>
                                );
                            }}
                        />
                        {processedData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={`url(#BalanceSheetCard-color-green)`} />
                        ))}
                    </Bar>
                    <Bar dataKey="dummyAssets" stackId="a" fill="#2ACF56" barSize={2} />
                    <Bar dataKey="liabilities" stackId="b" radius={[2, 2, 0, 0]}>
                        <LabelList
                            dataKey="liabilities"
                            position="top"
                            className="font-Inter font-semibold text-xs"
                            content={(props) => {
                                const { x, y, value } = props;
                                const formattedValue = Actions.trimNumber(value / 10000000, 1);
                                return (
                                    <text
                                        x={x + 20}
                                        y={y}
                                        dy={-8}
                                        fontSize="12"
                                        fill={value >= 0 ? "black" : "#DD2C2C"}
                                        textAnchor="middle"
                                        fontWeight={600}
                                    >
                                        {formattedValue}
                                    </text>
                                );
                            }}
                        />
                        {processedData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={`url(#BalanceSheetCard-color-red)`} />
                        ))}
                    </Bar>
                    <Bar dataKey="dummyLiabilities" stackId="b" fill="#F00" barSize={2} />
                    <defs>
                        <linearGradient
                            id="BalanceSheetCard-color-green"
                            xmlns="http://www.w3.org/2000/svg"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="100%"
                            spreadMethod="reflect"
                        >
                            <stop offset="0" stopColor="#BFF1CC" stopOpacity="1" />
                            <stop offset="1" stopColor="#F0FCF3" stopOpacity="1" />
                        </linearGradient>
                    </defs>
                    <defs>
                        <linearGradient
                            id="BalanceSheetCard-color-red"
                            xmlns="http://www.w3.org/2000/svg"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="100%"
                            spreadMethod="reflect"
                        >
                            <stop offset="0" stopColor="#FFB2B2" stopOpacity="1" />
                            <stop offset="1" stopColor="#FFF4F4" stopOpacity="1" />
                        </linearGradient>
                    </defs>
                </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-row justify-center gap-3">
                <DurationBtn
                    active={duration === 0}
                    text="Standalone"
                    onClick={() => {
                        setDuration(0);
                        fetchData(0);
                    }}
                />
                <DurationBtn
                    active={duration === 1}
                    text="Consolidated"
                    onClick={() => {
                        setDuration(1);
                        fetchData(1);
                    }}
                />
            </div>
        </div>
    );
}

export default memo(BalanceSheetCard);
